import { Card, FAIcon, Progress } from '@/components'
import { PlainLink } from '@/components_deprecated'
import { cn } from '@/lib/utils'
import { format } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subSeconds } from 'date-fns'
import { usePeriodProgressInfo } from './absence-period-report'
import type { AbsenceRequestNode } from './absence-requests'

interface Props {
  absence: AbsenceRequestNode
  action: 'approve' | 'reject' | null

  onApprove(): void
  onReject(): void
}

export function AbsenceRequestCard({
  absence,
  action,

  onApprove,
  onReject,
}: Props) {
  const translations = useTranslate({
    reject: 'absences.requests.reject',
    approve: 'absences.requests.approve',

    period: 'absences.requests.period',
    used: 'absences.requests.used',
    including: 'absences.requests.including-this',
  })

  const createProgressInfo = usePeriodProgressInfo()

  const [start, end] = (() => {
    const start = new Date(absence.datetimeStart)
    let end = new Date(absence.datetimeEnd)

    if (absence.absenceType?.durationMode === 'DAILY') end = subSeconds(end, 1)

    if (absence.absenceType?.durationMode === 'DAILY')
      return [format(start, 'PP'), format(end, 'PP')]

    return [format(start, 'PP, HH:mm'), format(end, 'PP, HH:mm')]
  })()

  const report =
    absence.absenceType?.preset === 'SIMPLE'
      ? null
      : absence.periodReport ?? null

  const progress = !report ? null : createProgressInfo(report)

  return (
    <Card
      key={absence.id}
      className={cn(
        'p-0 transition-shadow',
        !!action && 'ring-2',
        action === 'approve' ? 'ring-green-500' : 'ring-red-500'
      )}
      contentClassName="grow flex flex-col p-0"
    >
      <div
        className={cn(
          'flex flex-col grow py-3 px-4',
          'transition-opacity',
          !!action && 'opacity-40'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold hover:underline">
            <PlainLink to={`/absences/${absence.id}`}>
              {absence.user.fullName}
            </PlainLink>
          </div>

          {!!absence.absenceType && (
            <div className="flex items-center gap-2">
              <div className="font-medium text-sm">
                {absence.absenceType.name}
              </div>

              <div
                className="size-5 corner-squircle rounded-3xl"
                style={{ backgroundColor: absence.absenceType.color }}
              />
            </div>
          )}
        </div>

        <div className="text-sm whitespace-nowrap">
          {start} - {end}
        </div>

        {!!absence.reason && <p className="mt-2 text-sm">{absence.reason}</p>}
      </div>

      {!!report && (
        <div
          className={cn(
            'px-4 py-2 border-t border-t-gray-200',
            'transition-opacity',
            !!action && 'opacity-40'
          )}
        >
          <div className="flex gap-1 text-sm">
            <span className="font-semibold">{translations.period}</span>
            {format(new Date(report.periodStart), 'PP')} -{' '}
            {format(new Date(report.periodEnd), 'PP')}
          </div>

          {!!progress && (
            <div className="space-y-2 text-sm">
              <div className="flex gap-1">
                <span className="font-semibold">{translations.used}</span>

                <span>{progress.info}</span>

                <span className="font-medium text-gray-500">
                  {translations.including}
                </span>
              </div>

              {progress.value !== null && <Progress value={progress.value} />}

              {progress.warning && (
                <div className="flex gap-2 text-red-600">
                  <div>
                    <FAIcon icon="exclamation-triangle" type="solid" />
                  </div>
                  <span>{progress.warning}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 border-t border-t-gray-200">
        <button
          className={cn(
            'flex items-center justify-center gap-2 py-2',
            'rounded-bl-xl border-r border-r-gray-200 hover:bg-gray-50',
            'cursor-pointer text-xs',
            'hover:bg-gray-50',
            'transition-colors',
            action === 'reject' &&
              'text-white font-semibold bg-red-500 hover:bg-red-400'
          )}
          onClick={onReject}
        >
          <FAIcon
            className={cn('text-red-500', action === 'reject' && 'text-white')}
            icon="times"
          />
          {translations.reject}
        </button>

        <button
          className={cn(
            'flex items-center justify-center gap-2 py-2',
            'rounded-br-xl',
            'cursor-pointer text-xs',
            'hover:bg-gray-50',
            'transition-colors',
            action === 'approve' &&
              'text-white font-semibold bg-green-500 hover:bg-green-400'
          )}
          onClick={onApprove}
        >
          <FAIcon
            className={cn(
              'text-green-500',
              action === 'approve' && 'text-white'
            )}
            icon="check"
          />
          {translations.approve}
        </button>
      </div>
    </Card>
  )
}
